/**
 * API service for System Health Flow
 */

import { authFetch, API_BASE } from './client';
import { parseSystemGraphPayload } from '@/contracts/systemGraph';
import type {
  NodeStatus,
  FlowEdge,
  FlowNode,
  SystemGraphResponse,
  SystemBand,
  SystemLane,
  ViewMode,
} from '@/types/systemHealth';

export { SystemGraphPayloadError } from '@/contracts/systemGraph';

const SYSTEM_GRAPH_LANES_DEFAULT: SystemLane[] = [
  { id: 'sources', label: 'Sources', order: 0 },
  { id: 'data_store', label: 'Data Store', order: 1 },
  { id: 'feature_layer', label: 'Feature Layer', order: 2 },
  { id: 'decision_system', label: 'Oracle', order: 3 },
  { id: 'orchestration', label: 'Orchestration', order: 4 },
  { id: 'output', label: 'Output', order: 5 },
  { id: 'learning', label: 'Learning', order: 6 },
];

const SYSTEM_GRAPH_LANES_ARCHITECTURE: SystemLane[] = [
  ...SYSTEM_GRAPH_LANES_DEFAULT,
  { id: 'serving', label: 'Serving', order: 7 },
];

const SYSTEM_GRAPH_PRIMARY_TIMEOUT_MS = 7000;
const SYSTEM_GRAPH_FALLBACK_TIMEOUT_MS = 4500;
const SYSTEM_GRAPH_CLIENT_LKG_MAX = 32;
const SYSTEM_GRAPH_CLIENT_LKG_MAX_AGE_MS = 10 * 60_000;
const SYSTEM_GRAPH_SESSION_LKG_PREFIX = 'system-graph-lkg:';

type SystemGraphSource = 'e2e' | 'telemetry';
type AttemptError = { source: SystemGraphSource; message: string };

type ClientLkgEntry = {
  graph: SystemGraphResponse;
  cachedAtMs: number;
};

const systemGraphClientLkg = new Map<string, ClientLkgEntry>();

export class SystemGraphUnavailableError extends Error {
  attempts: AttemptError[];
  propertyId: string;
  mode: ViewMode;

  constructor(propertyId: string, mode: ViewMode, attempts: AttemptError[]) {
    const attemptSummary = attempts.length
      ? attempts.map((attempt) => `${attempt.source}: ${attempt.message}`).join('; ')
      : 'no live attempt details captured';
    super(
      `System graph unavailable for ${propertyId} (${mode}). ${attemptSummary}. No cached graph is available.`,
    );
    this.name = 'SystemGraphUnavailableError';
    this.attempts = attempts;
    this.propertyId = propertyId;
    this.mode = mode;
  }
}

function clientLkgKey(propertyId: string, mode: ViewMode): string {
  return `${propertyId}::${mode}`;
}

function sessionLkgKey(propertyId: string, mode: ViewMode): string {
  return `${SYSTEM_GRAPH_SESSION_LKG_PREFIX}${clientLkgKey(propertyId, mode)}`;
}

function canUseSessionStorage(): boolean {
  return typeof window !== 'undefined' && typeof window.sessionStorage !== 'undefined';
}

function removeSessionLkg(propertyId: string, mode: ViewMode): void {
  if (!canUseSessionStorage()) return;
  try {
    window.sessionStorage.removeItem(sessionLkgKey(propertyId, mode));
  } catch {
    // Best-effort cache only.
  }
}

function writeSessionLkg(propertyId: string, mode: ViewMode, entry: ClientLkgEntry): void {
  if (!canUseSessionStorage()) return;
  try {
    window.sessionStorage.setItem(sessionLkgKey(propertyId, mode), JSON.stringify(entry));
  } catch {
    // Best-effort cache only.
  }
}

function readSessionLkg(propertyId: string, mode: ViewMode): ClientLkgEntry | null {
  if (!canUseSessionStorage()) return null;
  try {
    const raw = window.sessionStorage.getItem(sessionLkgKey(propertyId, mode));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { graph?: unknown; cachedAtMs?: unknown };
    if (typeof parsed.cachedAtMs !== 'number') {
      removeSessionLkg(propertyId, mode);
      return null;
    }
    const graph = normalizeSystemGraph(parsed.graph);
    return { graph, cachedAtMs: parsed.cachedAtMs };
  } catch {
    removeSessionLkg(propertyId, mode);
    return null;
  }
}

function isExpiredLkg(entry: ClientLkgEntry): boolean {
  return Date.now() - entry.cachedAtMs > SYSTEM_GRAPH_CLIENT_LKG_MAX_AGE_MS;
}

function setClientLkg(propertyId: string, mode: ViewMode, graph: SystemGraphResponse): void {
  const key = clientLkgKey(propertyId, mode);
  const entry = { graph, cachedAtMs: Date.now() };
  if (systemGraphClientLkg.has(key)) {
    systemGraphClientLkg.delete(key);
  }
  systemGraphClientLkg.set(key, entry);
  writeSessionLkg(propertyId, mode, entry);
  if (systemGraphClientLkg.size > SYSTEM_GRAPH_CLIENT_LKG_MAX) {
    const oldest = systemGraphClientLkg.keys().next().value;
    if (oldest) {
      systemGraphClientLkg.delete(oldest);
    }
  }
}

function getClientLkg(propertyId: string, mode: ViewMode): SystemGraphResponse | null {
  const key = clientLkgKey(propertyId, mode);
  const entry = systemGraphClientLkg.get(key);
  if (entry) {
    if (isExpiredLkg(entry)) {
      systemGraphClientLkg.delete(key);
      removeSessionLkg(propertyId, mode);
      return null;
    }
    return entry.graph;
  }

  const sessionEntry = readSessionLkg(propertyId, mode);
  if (!sessionEntry) return null;
  if (isExpiredLkg(sessionEntry)) {
    systemGraphClientLkg.delete(key);
    removeSessionLkg(propertyId, mode);
    return null;
  }

  systemGraphClientLkg.set(key, sessionEntry);
  return sessionEntry.graph;
}

function formatAgeSeconds(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return 'unknown';
  if (seconds < 60) return '<1m';
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  if (seconds < 86400) return `${Math.round(seconds / 3600)}h`;
  return `${Math.round(seconds / 86400)}d`;
}

function freshnessLabel(
  status: NodeStatus,
  freshness_s: number | null,
  stale_after_s: number | null,
): string {
  if (status === 'idle') return 'idle';
  if (status === 'unknown') return 'unknown';
  if (status === 'active' && freshness_s === null) return 'running';
  if (freshness_s === null) return 'unknown';
  if (status === 'stale' && stale_after_s !== null) {
    return `stale (${formatAgeSeconds(freshness_s)} > ${formatAgeSeconds(stale_after_s)} threshold)`;
  }
  return `${formatAgeSeconds(freshness_s)} ago`;
}

function normalizeSystemGraph(raw: unknown): SystemGraphResponse {
  const { graph, warnings } = parseSystemGraphPayload(raw);

  if (warnings.length && import.meta.env.DEV) {
    console.warn('[system-graph] payload warnings:', warnings);
  }

  const nodes: FlowNode[] = graph.nodes.map((n) => ({
    id: n.id,
    lane: n.lane,
    label: n.label,
    sublabel: n.sublabel ?? undefined,
    status: n.status,
    freshness: freshnessLabel(n.status, n.freshness_s, n.stale_after_s),
    freshness_s: n.freshness_s,
    last_ok_at: n.last_ok_at,
    stale_after_s: n.stale_after_s,
    details: n.details,
    trace_id: n.trace_id,
    type: n.type,
    group: n.group ?? undefined,
    is_group_summary: n.is_group_summary ?? false,
    band: n.band ?? undefined,
  }));

  const edges: FlowEdge[] = graph.edges.map((e) => ({
    from: e.from,
    to: e.to,
    status: e.status,
    freshness_s: e.freshness_s,
    latency_ms_p95: e.latency_ms_p95,
    edge_type: e.edge_type === 'feedback' ? 'feedback' : null,
  }));

  const summary = nodes.reduce(
    (acc, n) => {
      acc.total_nodes += 1;
      acc[n.status] += 1;
      return acc;
    },
    {
      total_nodes: 0,
      healthy: 0,
      active: 0,
      stale: 0,
      error: 0,
      idle: 0,
      unknown: 0,
    },
  );

  // Prefer API-provided lanes (backend is source of truth).
  // Fall back to frontend defaults when the API doesn't include lanes.
  const rawRecord = raw as Record<string, unknown>;
  let lanes: SystemLane[];
  if (Array.isArray(rawRecord.lanes) && rawRecord.lanes.length > 0) {
    lanes = rawRecord.lanes
      .filter(
        (l): l is Record<string, unknown> =>
          typeof l === 'object' &&
          l !== null &&
          typeof (l as Record<string, unknown>).id === 'string' &&
          typeof (l as Record<string, unknown>).label === 'string' &&
          typeof (l as Record<string, unknown>).order === 'number',
      )
      .map((l) => ({
        id: l.id as string,
        label: l.label as string,
        order: l.order as number,
      }));
  } else {
    const hasArchLanes = nodes.some((n) => n.lane === 'serving' || n.lane === 'learning');
    lanes = hasArchLanes ? SYSTEM_GRAPH_LANES_ARCHITECTURE : SYSTEM_GRAPH_LANES_DEFAULT;
  }

  // Extract bands from raw payload if present (forward-compatible).
  let bands: SystemBand[] | undefined;
  if (Array.isArray(rawRecord.bands)) {
    bands = rawRecord.bands
      .filter(
        (b): b is Record<string, unknown> =>
          typeof b === 'object' &&
          b !== null &&
          typeof (b as Record<string, unknown>).id === 'string' &&
          typeof (b as Record<string, unknown>).label === 'string' &&
          typeof (b as Record<string, unknown>).order === 'number',
      )
      .map((b) => ({
        id: b.id as string,
        label: b.label as string,
        order: b.order as number,
      }));
  }

  return {
    property_id: graph.property_id ?? null,
    generated_at: graph.generated_at,
    lanes,
    ...(bands && bands.length > 0 ? { bands } : {}),
    nodes,
    edges,
    summary,
  };
}

function toErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message.trim()) {
    return error.message.trim();
  }
  return 'Unknown system graph error';
}

function withFallbackDetails(
  graph: SystemGraphResponse,
  options: {
    fallbackType: 'cached_lkg';
    mode: ViewMode;
    attempts: AttemptError[];
  },
): SystemGraphResponse {
  const { fallbackType, mode, attempts } = options;
  const fallbackMeta = {
    type: fallbackType,
    mode,
    attempts,
    fallback_at: new Date().toISOString(),
  };

  return {
    ...graph,
    nodes: graph.nodes.map((node) => ({
      ...node,
      details: {
        ...node.details,
        client_fallback: fallbackMeta,
      },
    })),
  };
}

/**
 * Fetch system graph data for a property
 *
 * @param propertyId - Property slug (e.g., 'soundry')
 * @param mode - View mode: 'health' | 'explain' | 'architecture'
 */
export async function fetchSystemGraph(
  propertyId: string,
  mode: ViewMode = 'health',
): Promise<SystemGraphResponse> {
  // The Data Flow Monitor is a telemetry page. In health mode we still keep
  // an E2E fallback, but telemetry should win when both are available.
  const preferredSource: SystemGraphSource = 'telemetry';
  const fallbackSource: SystemGraphSource | null = mode === 'health' ? 'e2e' : null;
  const attempts: AttemptError[] = [];

  const request = async (healthSource: SystemGraphSource, timeout: number) => {
    const response = await authFetch(
      `${API_BASE}/api/health/system-graph?property_id=${encodeURIComponent(propertyId)}&mode=${mode}&health_source=${healthSource}`,
      { timeout },
    );

    if (!response.ok) {
      const body = await response.text().catch(() => '');
      throw new Error(`System graph fetch failed: ${response.status}${body ? ` - ${body}` : ''}`);
    }

    const raw = (await response.json()) as unknown;
    const normalized = normalizeSystemGraph(raw);
    if (normalized.nodes.length === 0) {
      throw new Error(`System graph payload from ${healthSource} had no nodes`);
    }
    return normalized;
  };

  const attemptRequest = async (
    source: SystemGraphSource,
    timeout: number,
  ): Promise<SystemGraphResponse | null> => {
    try {
      const graph = await request(source, timeout);
      setClientLkg(propertyId, mode, graph);
      return graph;
    } catch (error) {
      attempts.push({ source, message: toErrorMessage(error) });
      return null;
    }
  };

  const primary = await attemptRequest(preferredSource, SYSTEM_GRAPH_PRIMARY_TIMEOUT_MS);
  if (primary) {
    return primary;
  }

  if (fallbackSource) {
    const fallback = await attemptRequest(fallbackSource, SYSTEM_GRAPH_FALLBACK_TIMEOUT_MS);
    if (fallback) {
      return fallback;
    }
  }

  const cached = getClientLkg(propertyId, mode);
  if (cached) {
    return withFallbackDetails(cached, {
      fallbackType: 'cached_lkg',
      mode,
      attempts,
    });
  }

  throw new SystemGraphUnavailableError(propertyId, mode, attempts);
}
